import dayjs from "dayjs";
import type { usePostHog } from "posthog-js/react";
import { useCallback, type Dispatch } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import usePublicApi from "@/core/api/usePublicApi";
import {
  AuthRequestOptions,
  AuthState,
  ForgotPasswordCredentials,
  ResetPasswordCodeCredentials,
} from "./auth";
import type { AuthAction } from "./auth-reducer";
import {
  getPasswordResetRequestError,
  getPasswordResetVerificationError,
  isThrottledApiError,
  RESEND_CODE_COOLDOWN_SECONDS,
} from "./password-reset-error";
import {
  normalizeResetEmail,
  PASSWORD_RESET_SOURCE_PARAM,
  WELCOME_EMAIL_PASSWORD_RESET_QUERY,
  WELCOME_EMAIL_PASSWORD_RESET_SOURCE,
} from "./password-reset-flow";
import { dismissThrottledResetToast } from "./throttled-reset-toast";

interface UsePasswordResetOptions {
  dispatch: Dispatch<AuthAction>;
  resetCodeResendAvailableAt: AuthState["resetCodeResendAvailableAt"];
  posthog: ReturnType<typeof usePostHog>;
}

export const usePasswordReset = ({
  dispatch,
  resetCodeResendAvailableAt,
  posthog,
}: UsePasswordResetOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const publicApi = usePublicApi();

  const getResetFlowQueryString = useCallback(() => {
    const source = new URLSearchParams(location.search).get(
      PASSWORD_RESET_SOURCE_PARAM,
    );
    if (source !== WELCOME_EMAIL_PASSWORD_RESET_SOURCE) {
      return "";
    }

    return WELCOME_EMAIL_PASSWORD_RESET_QUERY;
  }, [location.search]);

  const getResetCodeResendAvailableAt = useCallback(
    (email: string | null) =>
      email
        ? (resetCodeResendAvailableAt[normalizeResetEmail(email)] ?? null)
        : null,
    [resetCodeResendAvailableAt],
  );

  const openResetCodeEntry = useCallback(
    (email: string) => {
      dismissThrottledResetToast();
      dispatch({ type: "SET_FORGOT_PASSWORD_EMAIL", payload: email });
      const resetFlowQuery = getResetFlowQueryString();
      void navigate(
        resetFlowQuery
          ? `/reset-password-code?${resetFlowQuery}`
          : "/reset-password-code",
      );
    },
    [dispatch, getResetFlowQueryString, navigate],
  );

  const sendPasswordResetCode = useCallback(
    async (email: string, options?: AuthRequestOptions) => {
      const armResendCooldown = (seconds: number) => {
        dispatch({
          type: "SET_RESET_CODE_RESEND_AVAILABLE_AT",
          payload: { email, availableAt: dayjs().add(seconds, "second") },
        });
      };

      try {
        await publicApi.post(
          "/password_reset",
          { email },
          { signal: options?.signal },
        );
        armResendCooldown(RESEND_CODE_COOLDOWN_SECONDS);
        return null;
      } catch (error) {
        if (!isThrottledApiError(error)) {
          return getPasswordResetRequestError(error);
        }

        const seconds = error.retryAfterSeconds ?? RESEND_CODE_COOLDOWN_SECONDS;
        armResendCooldown(seconds);
        return getPasswordResetRequestError(error, seconds);
      }
    },
    [dispatch, publicApi],
  );

  const forgotPassword = useCallback(
    async (
      credentials: ForgotPasswordCredentials,
      options?: AuthRequestOptions,
    ) => {
      try {
        posthog?.capture("forgot_password_requested", {
          email: credentials.email,
        });
        const resetError = await sendPasswordResetCode(
          credentials.email,
          options,
        );
        if (resetError) throw resetError;
        posthog?.capture("forgot_password_email_sent", {
          email: credentials.email,
        });
        openResetCodeEntry(credentials.email);
      } catch (error) {
        posthog?.capture("forgot_password_failed", {
          email: credentials.email,
        });
        console.error(error);
        throw error;
      }
    },
    [posthog, openResetCodeEntry, sendPasswordResetCode],
  );

  const verifyResetPasswordCode = useCallback(
    async (
      credentials: ResetPasswordCodeCredentials,
      options?: AuthRequestOptions,
    ) => {
      try {
        await publicApi.post("/password_reset/verify", credentials, {
          signal: options?.signal,
        });
      } catch (error) {
        console.error(error);
        throw getPasswordResetVerificationError(error);
      }
    },
    [publicApi],
  );

  const resetPasswordCode = useCallback(
    async (credentials: ResetPasswordCodeCredentials) => {
      await verifyResetPasswordCode(credentials);
      dispatch({
        type: "SET_FORGOT_PASSWORD_EMAIL",
        payload: credentials.email,
      });
      dispatch({
        type: "SET_FORGOT_PASSWORD_VERIFICATION_CODE",
        payload: credentials.verification_code,
      });
      const resetFlowQuery = getResetFlowQueryString();
      void navigate(
        resetFlowQuery
          ? `/reset-password?${resetFlowQuery}`
          : "/reset-password",
      );
    },
    [verifyResetPasswordCode, dispatch, getResetFlowQueryString, navigate],
  );

  return {
    getResetCodeResendAvailableAt,
    openResetCodeEntry,
    sendPasswordResetCode,
    forgotPassword,
    verifyResetPasswordCode,
    resetPasswordCode,
  };
};
